import { getStackTrace, log } from '@src/Log.init';
import { rootApi } from './root.api';
import { setNTAdministrator } from 'features/session/sessionSlice';

// Per-request timeout for the user mutations, which send an invitation email and
// are far slower than a read: under the 3s global default (root.api.js) the
// client aborted a request the backend had already committed and reported a
// false failure (MIST-5468). Applied per endpoint rather than raising that
// default, so every other request keeps failing fast.
const USER_MUTATION_TIMEOUT = 15000;

export const userApi = rootApi.injectEndpoints({
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: (token) => ({
        url: 'me',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            { type: 'User', id: result._id },
            { type: 'User', id: 'ME' },
          ];
        }
        return [{ type: 'User', id: 'ME' }];
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        log.debug({ message_type: 'User', message: 'getCurrentUser: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'User',
            message: 'getCurrentUser: fulfilled',
            data: result.data,
          });
          dispatch(setNTAdministrator(result.data.nt_administrator));
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getCurrentUser: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    getUserEmailAddress: builder.query({
      query: (userId) => ({
        url: `user_email/${userId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            { type: 'User', id: result._id },
            { type: 'User', id: arg },
          ];
        }
        return [{ type: 'User', id: arg }];
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'User',
          message: 'getUserEmailAddress: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'User',
            message: 'getUserEmailAddress: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getUserEmailAddress: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    createUser: builder.mutation({
      query: (user) => ({
        url: 'user',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: user,
        timeout: USER_MUTATION_TIMEOUT,
      }),
      invalidatesTags: ['User'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({ message_type: 'User', message: 'createUser: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'User',
            message: 'createUser: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'createUser: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    reinviteUser: builder.mutation({
      query: (user) => ({
        url: `reinvite/${user._id}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: user,
        timeout: USER_MUTATION_TIMEOUT,
      }),
      invalidatesTags: ['User'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({ message_type: 'User', message: 'reinviteUser: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'User',
            message: 'reinviteUser: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'reinviteUser: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    updateUser: builder.mutation({
      query: (user) => ({
        url: 'user',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: user,
        timeout: USER_MUTATION_TIMEOUT,
      }),
      invalidatesTags: ['User'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({ message_type: 'User', message: 'updateUser: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'User',
            message: 'updateUser: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'updateUser: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    getClientUsers: builder.query({
      query: (clientId) => ({
        url: `users/${clientId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => {
        return [
          { type: 'User', id: 'LIST' },
          { type: 'User', id: arg },
        ];
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({ message_type: 'User', message: 'getClientUsers: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'User',
            message: 'getClientUsers: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getClientUsers: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useGetUserEmailAddressQuery,
  useCreateUserMutation,
  useReinviteUserMutation,
  useUpdateUserMutation,
  useGetClientUsersQuery,
  useLazyGetClientUsersQuery,
} = userApi;
